import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import {
  ADDRESS_POSTAL_CODE_PATTERN,
  ADDRESS_VOIVODESHIP_OPTIONS,
  AddressFormValueWithCoordinates,
  inferVoivodeshipFromPostalCodeValue,
  toAddressFormValueWithCoordinates,
} from '@shared/utils/address-form.utils';
import {
  InvestmentLocationData,
  buildInvestmentLocationDisplayAddress
} from '@features/investment/utils/investment-location.utils';
import { trimmedRequiredValidator } from '@shared/utils/forms/form-validators.util';

export type InvestmentLocationFormValue = Pick<
  AddressFormValueWithCoordinates,
  'postalCode' | 'city' | 'street' | 'voivodeship' | 'district' | 'commune' | 'latitude' | 'longitude'
>;

@Injectable()
export class InvestmentLocationInputModalFacade {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly voivodeshipOptions = [...ADDRESS_VOIVODESHIP_OPTIONS];

  readonly form = this.fb.group({
    postalCode: ['', [trimmedRequiredValidator(), Validators.pattern(ADDRESS_POSTAL_CODE_PATTERN)]],
    city: ['', [trimmedRequiredValidator()]],
    street: ['', [trimmedRequiredValidator()]],
    voivodeship: ['', [trimmedRequiredValidator()]],
    district: ['', [trimmedRequiredValidator()]],
    commune: ['', [trimmedRequiredValidator()]],
    latitude: [null as number | null],
    longitude: [null as number | null]
  });

  constructor() {
    this.bindVoivodeshipInference();
  }

  patchInitialData(initialData: InvestmentLocationData | null): void {
    this.form.reset(this.toFormValue(initialData));
  }

  buildLocationData(): InvestmentLocationData {
    return this.buildLocationDataFromValue(this.form.getRawValue() as InvestmentLocationFormValue);
  }

  buildLocationDataFromValue(raw: InvestmentLocationFormValue): InvestmentLocationData {
    const normalized = toAddressFormValueWithCoordinates(raw);

    return {
      postalCode: normalized.postalCode,
      city: normalized.city,
      street: normalized.street,
      voivodeship: normalized.voivodeship,
      district: normalized.district,
      commune: normalized.commune,
      latitude: normalized.latitude ?? undefined,
      longitude: normalized.longitude ?? undefined,
      displayAddress: buildInvestmentLocationDisplayAddress({
        postalCode: normalized.postalCode,
        city: normalized.city,
        street: normalized.street,
      })
    };
  }

  toFormValue(initialData: InvestmentLocationData | null): InvestmentLocationFormValue {
    return toAddressFormValueWithCoordinates(initialData);
  }

  private bindVoivodeshipInference(): void {
    this.form.controls.postalCode.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((postalCode) => {
        const inferred = inferVoivodeshipFromPostalCodeValue(postalCode);
        if (!inferred) {
          return;
        }

        this.form.controls.voivodeship.patchValue(inferred, { emitEvent: false });
      });
  }
}
